import { useState } from "react";
import { ButtonAction, ButtonPrimary } from "../../components/NewButtons";
import { CreateTimeslotsModal } from "./CreateTimeslotsModal";
import { ConfirmActionModal } from "../../components/ConfirmActionModal";
import { useCreateTimeslotTemplate } from "./service/shop-api";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import * as authSelectors from "../auth/authSelectors";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { TimeslotTemplateCreate } from "./model/TimeslotTemplatesSuccessResponse";
import { TimeslotDays } from "./model/TimeslotTemplatesSuccessResponse";
import { minutesToSeconds, secondsToMinutes } from "../../util";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../error-handler";
import { ActionSpinner } from "../../components/ActionSpinner";
import { capitalize } from "lodash";

export type CreateTimeslotsFormValues = Omit<TimeslotTemplateCreate, "days"> & {
  days: { value: TimeslotDays }[];
  customDuration: number;
};

const formatDays = (days: { value: TimeslotDays }[]) => {
  return days.map((day) => capitalize(day.value)).join(", ");
};

export const CreateTimeslotsSelector = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [formIsOpen, setFormIsOpen] = useState(false);
  const [confirmIsOpen, setConfirmIsOpen] = useState(false);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const schema = yup.object({
    days: yup.array().min(1, "Please select at least one day"),
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
    duration: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test("Testing if above zero", "Must be at least 0", (value) => {
        if (showCustomDuration) {
          return true;
        }
        return !!value && value >= 1;
      }),
    maxOrders: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Max orders must be at least 0"),
    maxUnits: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Max units must be at least 0"),
    maxDeliveryOrders: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Max delivery orders must be at least 0"),
    maxPickupOrders: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Max pickup orders must be at least 0"),
    preparationTime: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Preparation time must be at least 0"),
    deliveryTime: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .min(0, "Delivery time must be at least 0"),
    customDuration: yup
      .number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test("Testing if above zero", "Must be at least 1", (value) => {
        if (!showCustomDuration) {
          return true;
        }
        return !!value && value >= 1;
      }),
  });

  const methods = useForm<CreateTimeslotsFormValues>({
    defaultValues: {
      days: [],
      duration: minutesToSeconds(5),
      customDuration: 0,
      maxOrders: 0,
      maxUnits: 0,
      maxDeliveryOrders: 0,
      maxPickupOrders: 0,
      preparationTime: 0,
      deliveryTime: 0,
    },
    resolver: yupResolver(schema),
  });

  const {
    mutateAsync: createTimeslotTemplate,
    isLoading: isCreatingTimeslotTemplate,
  } = useCreateTimeslotTemplate(shopId);

  const onSubmit = async (data: CreateTimeslotsFormValues) => {
    const {
      duration,
      customDuration,
      days,
      startTime,
      endTime,
      ...rest
    } = data;
    if (!shopId) {
      return;
    }
    try {
      await createTimeslotTemplate({
        organizationKeyName,
        data: {
          shopId,
          ...rest,
          days: days.map((day) => day.value),
          // Sending with seconds, but avoiding letting user be able to input seconds
          startTime: `${startTime}:00`,
          endTime: `${endTime}:00`,
          duration: showCustomDuration
            ? minutesToSeconds(customDuration)
            : duration,
        },
      });
      setConfirmIsOpen(false);
      setFormIsOpen(false);
      toastr.success(
        "Success",
        "Timeslots created successfully. It can take up to 1 minutes before you can see your created timeslots. Refresh if they do not appear."
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const startTime = methods.watch("startTime");
  const endTime = methods.watch("endTime");
  const days = methods.watch("days");
  const duration = methods.watch("duration");
  const customDuration = methods.watch("customDuration");

  return (
    <FormProvider {...methods}>
      <ButtonPrimary type="button" onClick={() => setFormIsOpen(true)}>
        Create timeslots
      </ButtonPrimary>
      <CreateTimeslotsModal
        isOpen={formIsOpen}
        setShowCustomDuration={setShowCustomDuration}
        showCustomDuration={showCustomDuration}
        onSubmit={async () => {
          const isValid = await methods.trigger();
          if (!isValid) {
            return;
          }
          setFormIsOpen(false);
          setConfirmIsOpen(true);
        }}
        onClose={() => {
          methods.reset();
          setFormIsOpen(false);
        }}
      />
      <ConfirmActionModal
        title="Are you sure you want to create timeslots?"
        subtitle={`You will create timeslots for ${formatDays(
          days
        )} between ${startTime} and ${endTime} with an interval of ${
          showCustomDuration ? customDuration : secondsToMinutes(duration)
        } minutes. This will overwrite all existing timeslots on the days you are creating timeslots for.`}
        isOpen={confirmIsOpen}
        width={600}
        onClose={() => {
          setConfirmIsOpen(false);
          setFormIsOpen(true);
        }}
        confirmButton={
          <ButtonAction
            type="button"
            onClick={methods.handleSubmit(onSubmit)}
            disabled={isCreatingTimeslotTemplate}
          >
            {isCreatingTimeslotTemplate ? <ActionSpinner /> : "Create"}
          </ButtonAction>
        }
      ></ConfirmActionModal>
    </FormProvider>
  );
};
